import React from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from '../../store/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadConversationAction } from '../../store/conversations/conversationSlice';
import { IOrderOption } from '../../types/accounting/IOrderOption';
import { eContactType } from '../../types/conversations/eContactType';
import { ContactOptionsType, IConversationContactData } from '../../types/conversations/IConversationContact';
import { getOrdersSearchAction } from '../../store/orders';
import { eSearchOrdersOrderBy } from '../../ui-toolkit/orders/searchOrder/types/eSearchOrdersOrderBy';
import { EditConversationForm } from './EditConversationForm';
import { IConversationOrderData } from '../../types/conversations/IConversation';
import { useAppDispatch } from '../../store/store';
import {
  useListPersonOrderContactsQuery,
  useListSignatoryOrderContactsQuery,
  useListClientOrderContactsQuery,
  useListLenderOrderContactsQuery,
  useListThirdPartyContactsQuery,
} from '../../API/order/OrderContactsAPI';

const prettyContactType: Record<eContactType, string> = {
  [eContactType.Person]: 'People',
  [eContactType.Signatory]: 'Signatories',
  [eContactType.ClientContact]: 'Client Contacts',
  [eContactType.LenderContact]: 'Lender Contacts',
  [eContactType.ThirdPartyContact]: 'Third Party Contacts',
  [eContactType.None]: 'None',
};

const getContactLabel = ({ firstName, lastName, purpose }: IConversationContactData): string => {
  const purposeLabel = purpose ? ' - ' + purpose.charAt(0).toUpperCase() + purpose.slice(1) : '';
  return `${firstName} ${lastName}${purposeLabel}`;
};

interface IEditConversationContainerProps {
  conversationId: string;
  onCancel?: () => void;
  onSubmit: (conversationOrder: IConversationOrderData) => void;
  saveButtonText: string;
  autoSave?: boolean;
}

export const EditConversationContainer: React.FC<IEditConversationContainerProps> = ({
  conversationId,
  onCancel,
  onSubmit,
  saveButtonText,
  autoSave,
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (conversationId) {
      dispatch(loadConversationAction(conversationId));
    }
  }, [conversationId, dispatch]);

  const [selectedOrder, setSelectedOrder] = useState<IOrderOption>();

  const { data: conversationOrder } = useSelector((state: RootStore) => state.conversations.conversationOrder);
  const { data: conversation } = useSelector((state: RootStore) => state.conversations.conversation);
  const orderInfo = useSelector((state: RootStore) => state.order.orderInformationSlice);

  const orderId = conversationOrder?.orderId || '';
  const { data: clientContacts = [] } = useListClientOrderContactsQuery(orderId, { skip: !orderId });
  const { data: lenderContacts = [] } = useListLenderOrderContactsQuery(orderId, { skip: !orderId });
  const { data: thirdPartyContacts = [] } = useListThirdPartyContactsQuery(orderId, { skip: !orderId });
  const { data: persons = [] } = useListPersonOrderContactsQuery(orderId, { skip: !orderId });
  const { data: signatories = [] } = useListSignatoryOrderContactsQuery(orderId, { skip: !orderId });

  const contactOptions = useMemo(
    () => ({
      [eContactType.Person]: persons,
      [eContactType.Signatory]: signatories,
      [eContactType.ClientContact]: clientContacts,
      [eContactType.LenderContact]: lenderContacts,
      [eContactType.ThirdPartyContact]: thirdPartyContacts,
    }),
    [persons, signatories, clientContacts, lenderContacts, thirdPartyContacts]
  );

  const contacts: ContactOptionsType = useMemo(
    () =>
      [
        eContactType.Person,
        eContactType.Signatory,
        eContactType.ClientContact,
        eContactType.LenderContact,
        eContactType.ThirdPartyContact,
      ].map((contactType) => ({
        label: prettyContactType[contactType],
        options: contactOptions[contactType].map((party: IConversationContactData) => ({
          label: getContactLabel(party),
          value: { ...party, contactType },
        })),
      })),
    [contactOptions]
  );

  const getNewContactOptions = useCallback(
    ({ orderId }: { orderId?: string }) => {
      setSelectedOrder((prev) => {
        if (orderId && prev?.data.id !== orderId) {
          return prev;
        }
        return prev;
      });
    },
    [],
  );

  const onOrderSelect = useCallback(
    (order?: IOrderOption) => {
      const orderId = order?.data.id;
      if (orderId && selectedOrder?.data.id !== orderId) {
        setSelectedOrder(order);
        getNewContactOptions({ orderId });
      }
    },
    [selectedOrder, getNewContactOptions],
  );

  useEffect(() => {
    const orderId = conversationOrder?.orderId;
    const fileNumber: string | undefined | null = orderId && orderInfo[orderId]?.data?.transaction.file_number;

    dispatch(
      getOrdersSearchAction({
        fileNumbers: fileNumber ? [fileNumber] : [],
        sortBy: eSearchOrdersOrderBy.createdAtDESC,
        pageNumber: 0,
        pageSize: 5,
      })
    );
  }, [dispatch, conversationOrder?.orderId, orderInfo]);

  return (
    <>
      {conversationOrder && (
        <EditConversationForm
          conversationValue={conversation.channelValue}
          conversationType={conversation.channelType}
          contacts={contacts}
          conversationOrder={conversationOrder}
          disabled={false}
          onCancel={onCancel}
          onSubmit={onSubmit}
          onOrderSelect={onOrderSelect}
          saveButtonText={saveButtonText}
          autoSave={autoSave}
        />
      )}
    </>
  );
};
